import React, { ChangeEvent, DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';
import Select, { SingleValue } from 'react-select';
import { faArrowRight, faCloudUploadAlt, faFileImport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomButton } from '../../components/CustomButton';
import {
    getPassportByPassportIdAndType,
    getPassportsByDescriptions,
    importWarehousePdfDocument,
    updatePassportDescriptionMapping,
    useGetAllShortPassports as getAllShortPassports,
} from '../../utils/warehouses-hooks';
import { ConsumableTrackingType, PassportItemType, ShortDevicePassport } from '../../models/warehouse-models';
import { getGeneratedBatchCode } from '../../utils/batch-code';
import { getWarehouseImportConflictMessage } from '../../utils/warehouse-import-error';
import { toast } from 'react-toastify';

interface ParsedWarehousePdfItem {
    deliveryPrice: string;
    description: string;
    hasContainer?: boolean;
    quantity: string;
    quantityUnit: QuantityUnit;
}

interface ParsedWarehousePdfMetadata {
    documentNumber?: string;
    receiptInfo?: string;
    supplierName?: string;
}

interface PassportLookupResult {
    description?: string;
    emptyContainerPassportId?: number | null;
    emptyContainerPassportName?: string | null;
    id?: number;
    name?: string;
    oilCapacity?: number;
    passport?: string;
    passportId?: string;
    passportName?: string;
    supportedContainerType?: string | null;
    trackingType?: ConsumableTrackingType;
    type?: PassportItemType | string;
}

interface WarehousePdfImportPayloadItem {
    batchCode: string;
    deliveryPrice: string;
    description: string;
    passport?: {
        id?: number;
        name?: string;
        passportId?: string;
    };
    quantity: number;
    quantityUnit: QuantityUnit;
    hasContainer?: boolean;
}

interface WarehousePdfImportPayload {
    documentNumber?: string;
    items: WarehousePdfImportPayloadItem[];
    supplierName?: string;
    warehouseId: number;
}

interface WarehousePdfImportInvalidItem {
    conflictingPassport?: string;
    conflictingPassportId?: number;
    description: string;
    index: number;
    passport?: string;
    passportId?: number;
}

interface WarehousePdfImportErrorResponse {
    error?: string;
    invalidItems?: WarehousePdfImportInvalidItem[];
}

interface PassportMappingCorrectionModalState {
    conflict: WarehousePdfImportInvalidItem;
    description: string;
    index: number;
    selectedPassport: ShortDevicePassport | null;
}

interface PdfTextLine {
    items: Array<{ text: string; x: number }>;
    pageNumber: number;
    text: string;
    y: number;
}

interface LastItemLinePosition {
    pageNumber: number;
    y: number;
}

interface WarehousePdfImportProps {
    buttonClass?: string;
    hideButton?: boolean;
    importFileRef?: React.MutableRefObject<((file: File) => Promise<void>) | null>;
    onImportSuccess?: () => Promise<void> | void;
    openFileDialogRef?: React.MutableRefObject<(() => void) | null>;
    warehouseId: number;
}

type QuantityUnit = 'COUNT' | 'ML';

const countUnit: QuantityUnit = 'COUNT';
const mlUnit: QuantityUnit = 'ML';
const quantityRegExp = /(?:^|\s)(\d+(?:[,.]\d+)?)\s*(?:БР|бр\.?|BR|PCS|PC|QTY|брой|броя)(?=\s|$)/i;
const quantityInDescriptionRegExp =
    /(?:^|\s)\d+(?:[,.]\d+)?\s*(?:БР|бр\.?|BR|PCS|PC|QTY|брой|броя)(?=\s|$)/gi;
const supplierLabelRegExp = /(?:доставчик|supplier)\s*[:-]?/i;
const supplierRegExp = /(?:доставчик|supplier)\s*[:-]?\s*(.+)/i;
const companyNameRegExp = /(?:ЕООД|ООД|АД|ЕАД|ЕТ|LTD|LLC|INC|GMBH)/i;
const receiptRegExp = /\b(?:стокова(?:\s+разписка)?|разписка|receipt|delivery\s+note)\b/i;
const tableHeaderRegExp = /(?:описание|наименование|баркод|к-во|количество|qty|quantity|description|name|barcode)/i;
const documentNumberLineRegExp =
    /(?:ф-?ра|фактура|invoice)|(?:no\.?|№|номер)\s*(?:на\s*)?(?:документ|document)|(?:document|документ)\s*(?:no\.?|№|number|номер)/i;
const invoiceDocumentNumberValueRegExp =
    /(?:ф-?ра|фактура|invoice)\s*(?:(?:no\.?|№|number|номер)\s*)?[:#-]?\s*(\d[\d_./-]*)/i;
const customerOrderDocumentNumberValueRegExp =
    /(?:no\.?|№)?\s*пор\s*\.?\s*клиент\s*(?:no\.?|№)?\s*(\d[\d_./-]*)/i;
const markedDocumentNumberValueRegExp = /(?:^|\s)(?:no\.?\s+|№\s*)(\d[\d_./-]*)/i;
const namedDocumentNumberValueRegExp =
    /(?:номер)\s*(?:на\s*)?(?:документ)?\s*[:#-]?\s*(\d[\d_./-]*)|(?:document|документ)\s*(?:no\.?|№|number|номер)?\s*[:#-]?\s*(\d[\d_./-]*)/i;
const standaloneDocumentNumberValueRegExp = /^\s*(\d{4,}[\d_./-]*)\b/;
const descriptionHeaderRegExp = /(?:описание|наименование|description|name|item)/i;
const descriptionHeaderItemRegExp = /(?:описание|наименование|description|name|item)/i;
const measureHeaderRegExp = /(?:мярка|м-ка|measure|unit)/i;
const deliveryPriceHeaderRegExp = /(?:доставна\s+цена|дост\.?\s*цена|ед\.?\s*цена|delivery\s+price|unit\s+price|price|цена)/i;
const deliveryPriceHeaderItemRegExp = /(?:достав|дост\.?|ед\.?\s*цена|delivery|unit|price|цена)/i;
const quantityHeaderRegExp = /(?:к-во|количество|qty|quantity|брой|броя)/i;
const decimalNumberRegExp = /\b\d+(?:\s\d{3})*(?:[,.]\d+)?\b/g;
const positionedItemLineRegExp = /^\s*\d+\s+\S+/;
const measureValueRegExp = /^(?:бр\.?|бр|pcs|pc)$/i;
const leadingArticleCodeRegExp = /^\s*(?:[A-ZА-Я]*\d+[A-ZА-Я0-9_./-]*|[A-ZА-Я]+-[A-ZА-Я0-9_./-]+)\s+/i;
const lettersRegExp = /[A-ZА-Я]/i;
const leadingPositionRegExp = /^\s*\d+\s+/;
const priceWithCurrencyRegExp = /\s+\d+(?:[,.]\d+)?\s*(?:EUR|BGN|USD|лв\.?|лева)\b/i;
const invoiceFooterRegExp =
    /(?:^|\s)(?:име\s*подпис|подпис|фактура|оригинал|стр\.?|страница|поз\.?|клиента|единица|сума|стойност|словом|получател|съставил|срок|плащане|ддс|signature|invoice|page|total|amount|recipient|payment)(?=\s|[:.]|$)/i;
const footerMarkerBeforeFooterRegExp =
    /\s+[xх×]\s+(?=име\s*подпис|подпис|фактура|оригинал|стр\.?|страница|поз\.?|клиента|единица|сума|стойност|словом|получател|съставил|срок|плащане|ддс|signature|invoice|page|total|amount|recipient|payment)/i;
const footerMarkerOnlyRegExp = /^[xх×]\s*[,;:.]?\s*$/i;
const trailingFooterMarkerRegExp = /\s+[xх×]\s*[,;:.]?\s*$/i;

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const getBatchCode = () => {
    return getGeneratedBatchCode('L');
};

const isTextItem = (item: unknown): item is TextItem => {
    return !!item && typeof item === 'object' && 'str' in item && 'transform' in item;
};

const getHeaderColumnX = (line: PdfTextLine, regExp: RegExp, itemRegExp = regExp) => {
    const headerItem = line.items.find((item) => itemRegExp.test(item.text));

    if (headerItem) {
        return headerItem.x;
    }

    return regExp.test(line.text) ? line.items[0]?.x : undefined;
};

const findClosestNumberCandidate = (
    line: PdfTextLine,
    columnX: number | undefined,
    excludedValues: string[] = [],
) => {
    if (columnX === undefined) {
        return undefined;
    }

    const excluded = excludedValues.map((value) => value.trim()).filter(Boolean);
    const matches = line.items.flatMap((item) =>
        [...item.text.matchAll(decimalNumberRegExp)]
            .filter((match) => !excluded.includes(match[0]))
            .map((match) => ({
                text: match[0],
                x: item.x,
            })),
    );

    return matches.reduce<{ text: string; x: number } | undefined>((closestMatch, match) => {
        if (!closestMatch || Math.abs(match.x - columnX) < Math.abs(closestMatch.x - columnX)) {
            return match;
        }

        return closestMatch;
    }, undefined);
};

const parsePdfNumber = (value: string) => Number(value.replace(/\s/g, '').replace(',', '.'));
const normalizeDecimalValue = (value: string) => value.replace(/\s/g, '').replace(/,/g, '.');
const isOilPassport = (passport?: ShortDevicePassport | null) => passport?.type === 'OIL';
const supportsBottle = (passport?: ShortDevicePassport | null) =>
    passport?.type === 'DEVICE'
    && passport.supportedContainerType === 'BOTTLE'
    && passport.emptyContainerPassportId != null;
const isLiquidMillilitrePassport = (passport?: ShortDevicePassport | null) =>
    isOilPassport(passport) && passport?.trackingType === 'LIQUID_ML';
const getImportQuantity = (quantity: string) => {
    const parsedQuantity = Number(normalizeDecimalValue(quantity));
    return parsedQuantity || 0;
};

const isCloseToColumn = (x: number, columnX: number | undefined, tolerance = 45) => {
    return columnX !== undefined && Math.abs(x - columnX) <= tolerance;
};

const getColumnText = (
    line: PdfTextLine,
    columnX: number | undefined,
    nextColumnXs: Array<number | undefined>,
    leadingTolerance = 0,
) => {
    if (columnX === undefined) {
        return '';
    }

    const startColumnX = columnX - leadingTolerance;
    const nextColumnX = nextColumnXs
        .filter((nextX): nextX is number => nextX !== undefined && nextX > columnX)
        .sort((firstX, secondX) => firstX - secondX)[0];

    return line.items
        .filter((item) => item.x >= startColumnX && (nextColumnX === undefined || item.x < nextColumnX))
        .map((item) => item.text)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const getLineQuantity = (line: PdfTextLine, quantityColumnX: number | undefined) => {
    if (positionedItemLineRegExp.test(line.text)) {
        const quantityCandidate = findClosestNumberCandidate(line, quantityColumnX);

        if (quantityCandidate && isCloseToColumn(quantityCandidate.x, quantityColumnX)) {
            return {
                quantityText: quantityCandidate.text,
                quantityValue: parsePdfNumber(quantityCandidate.text),
            };
        }
    }

    const quantityMatch = line.text.match(quantityRegExp);

    if (quantityMatch) {
        return {
            quantityText: quantityMatch[0],
            quantityValue: parsePdfNumber(quantityMatch[1]),
        };
    }

    if (!positionedItemLineRegExp.test(line.text)) {
        return undefined;
    }

    const quantityCandidate = findClosestNumberCandidate(line, quantityColumnX);

    if (!quantityCandidate || !isCloseToColumn(quantityCandidate.x, quantityColumnX)) {
        return undefined;
    }

    return {
        quantityText: quantityCandidate.text,
        quantityValue: parsePdfNumber(quantityCandidate.text),
    };
};

const getMeasureText = (line: PdfTextLine, measureColumnX: number | undefined) => {
    if (measureColumnX === undefined) {
        return '';
    }

    return line.items.find((item) => isCloseToColumn(item.x, measureColumnX, 12) && measureValueRegExp.test(item.text))
        ?.text;
};

const getFallbackDescription = (
    line: PdfTextLine,
    quantityText: string,
    deliveryPriceText?: string,
) => {
    return line.text
        .replace(quantityText, '')
        .replace(deliveryPriceText || '', '')
        .replace(/\s+/g, ' ')
        .trim();
};

const cleanParsedDescription = (description: string) => {
    const descriptionWithoutFooterMarker = description.replace(footerMarkerBeforeFooterRegExp, ' ');
    const footerMatch = descriptionWithoutFooterMarker.match(invoiceFooterRegExp);
    const descriptionBeforeFooter = footerMatch
        ? descriptionWithoutFooterMarker.slice(0, footerMatch.index)
        : descriptionWithoutFooterMarker;
    const priceMatch = descriptionBeforeFooter.match(priceWithCurrencyRegExp);
    const descriptionBeforePrices = priceMatch
        ? descriptionBeforeFooter.slice(0, priceMatch.index)
        : descriptionBeforeFooter;

    return descriptionBeforePrices
        .replace(quantityInDescriptionRegExp, ' ')
        .replace(leadingPositionRegExp, '')
        .replace(leadingArticleCodeRegExp, '')
        .replace(trailingFooterMarkerRegExp, '')
        .replace(/\s+/g, ' ')
        .trim();
};

const getDescriptionTextBeforeMeasure = (
    line: PdfTextLine,
    descriptionColumnX: number | undefined,
    measureColumnX: number | undefined,
) => {
    if (measureColumnX === undefined) {
        return '';
    }

    const startX = descriptionColumnX !== undefined ? descriptionColumnX - 110 : 40;

    return line.items
        .filter((item) => item.x >= startX && item.x < measureColumnX)
        .map((item) => item.text)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const getNearbyDescriptionText = (
    lines: PdfTextLine[],
    lineIndex: number,
    line: PdfTextLine,
    descriptionColumnX: number | undefined,
    measureColumnX: number | undefined,
) => {
    return lines
        .filter(
            (nearbyLine, nearbyLineIndex) =>
                nearbyLineIndex !== lineIndex &&
                nearbyLine.pageNumber === line.pageNumber &&
                Math.abs(nearbyLine.y - line.y) <= 8 &&
                !getMeasureText(nearbyLine, measureColumnX),
        )
        .sort((firstLine, secondLine) => secondLine.y - firstLine.y)
        .map((nearbyLine) => getDescriptionTextBeforeMeasure(nearbyLine, descriptionColumnX, measureColumnX))
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const getDescriptionContinuation = (
    line: PdfTextLine,
    descriptionColumnX: number | undefined,
    measureColumnX: number | undefined,
    lastItemLinePosition: LastItemLinePosition | undefined,
) => {
    const continuation = cleanParsedDescription(line.text);
    const lineStartX = line.items[0]?.x;

    if (
        !lastItemLinePosition ||
        line.pageNumber !== lastItemLinePosition.pageNumber ||
        lastItemLinePosition.y - line.y > 18 ||
        lastItemLinePosition.y <= line.y
    ) {
        return '';
    }

    if (
        descriptionColumnX !== undefined &&
        (lineStartX === undefined ||
            lineStartX < descriptionColumnX - 45 ||
            (measureColumnX !== undefined && lineStartX >= measureColumnX))
    ) {
        return '';
    }

    if (
        !continuation ||
        footerMarkerOnlyRegExp.test(continuation) ||
        !lettersRegExp.test(continuation) ||
        leadingPositionRegExp.test(line.text) ||
        quantityRegExp.test(line.text) ||
        priceWithCurrencyRegExp.test(line.text) ||
        invoiceFooterRegExp.test(continuation) ||
        tableHeaderRegExp.test(continuation) ||
        supplierRegExp.test(continuation) ||
        receiptRegExp.test(continuation) ||
        documentNumberLineRegExp.test(continuation)
    ) {
        return '';
    }

    return continuation;
};

const getDescriptionKey = (description: string) => description.trim().toLowerCase();

const dedupeParsedItems = (items: ParsedWarehousePdfItem[]) => {
    const parsedItemsByKey = new Map<string, ParsedWarehousePdfItem>();

    items.forEach((item) => {
        const itemKey = `${getDescriptionKey(item.description)}|${item.deliveryPrice}|${item.quantity}`;

        if (!parsedItemsByKey.has(itemKey)) {
            parsedItemsByKey.set(itemKey, item);
        }
    });

    return Array.from(parsedItemsByKey.values());
};

const parsePdfLines = (lines: PdfTextLine[]): ParsedWarehousePdfItem[] => {
    let descriptionColumnX: number | undefined;
    let measureColumnX: number | undefined;
    let deliveryPriceColumnX: number | undefined;
    let quantityColumnX: number | undefined;
    let lastItemLinePosition: LastItemLinePosition | undefined;

    const items = lines.reduce<ParsedWarehousePdfItem[]>((parsedItems, line, lineIndex) => {
        descriptionColumnX =
            getHeaderColumnX(line, descriptionHeaderRegExp, descriptionHeaderItemRegExp) ?? descriptionColumnX;
        measureColumnX = getHeaderColumnX(line, measureHeaderRegExp) ?? measureColumnX;
        deliveryPriceColumnX =
            getHeaderColumnX(line, deliveryPriceHeaderRegExp, deliveryPriceHeaderItemRegExp) ?? deliveryPriceColumnX;
        quantityColumnX = getHeaderColumnX(line, quantityHeaderRegExp) ?? quantityColumnX;

        const quantityInfo = getLineQuantity(line, quantityColumnX);

        if (!quantityInfo) {
            const continuation = getDescriptionContinuation(
                line,
                descriptionColumnX,
                measureColumnX,
                lastItemLinePosition,
            );

            if (continuation && parsedItems.length > 0) {
                const lastItem = parsedItems[parsedItems.length - 1];
                lastItem.description = cleanParsedDescription(`${lastItem.description} ${continuation}`);
                lastItemLinePosition = {
                    pageNumber: line.pageNumber,
                    y: line.y,
                };
            }

            return parsedItems;
        }

        const { quantityText, quantityValue } = quantityInfo;
        const deliveryPriceMatch = findClosestNumberCandidate(line, deliveryPriceColumnX, [quantityText]);
        const columnDescription = getColumnText(
            line,
            descriptionColumnX,
            [measureColumnX, deliveryPriceColumnX, quantityColumnX],
            45,
        );
        const rowDescription = getDescriptionTextBeforeMeasure(line, descriptionColumnX, measureColumnX);
        const nearbyDescription = getNearbyDescriptionText(lines, lineIndex, line, descriptionColumnX, measureColumnX);
        const reconstructedDescription = [rowDescription, nearbyDescription]
            .filter(Boolean)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
        const description = cleanParsedDescription(
            columnDescription ||
                reconstructedDescription ||
                getFallbackDescription(line, quantityText, deliveryPriceMatch?.text),
        );

        if (!description) {
            return parsedItems;
        }

        parsedItems.push({
            deliveryPrice: deliveryPriceMatch?.text?.replace(',', '.') || '',
            description,
            quantity: String(quantityValue),
            quantityUnit: countUnit,
        });
        lastItemLinePosition = {
            pageNumber: line.pageNumber,
            y: line.y,
        };

        return parsedItems;
    }, []);

    return dedupeParsedItems(items);
};

const cleanPdfMetadataValue = (value: string) => {
    return value
        .replace(/^[\s:/-]+/, '')
        .replace(/\s+/g, ' ')
        .replace(/\s*[,;]\s*$/, '')
        .trim();
};

const cleanDocumentNumber = (value: string) => {
    return cleanPdfMetadataValue(value).replace(/^(?:no\.?|№|номер)\s*/i, '').trim();
};

const isUsableSupplierName = (value: string) => {
    return !!value && !tableHeaderRegExp.test(value);
};

const getDocumentNumberFromLine = (line: string, includeCustomerOrder = false) => {
    const documentNumberMatch =
        line.match(invoiceDocumentNumberValueRegExp) ||
        line.match(markedDocumentNumberValueRegExp) ||
        line.match(namedDocumentNumberValueRegExp) ||
        (includeCustomerOrder ? line.match(customerOrderDocumentNumberValueRegExp) : undefined);
    const documentNumber = cleanDocumentNumber(
        documentNumberMatch?.[1] || documentNumberMatch?.[2] || '',
    );

    return documentNumber && /\d/.test(documentNumber) ? documentNumber : '';
};

const parseDocumentNumber = (lines: string[]) => {
    const tableHeaderIndex = lines.findIndex((line) => tableHeaderRegExp.test(line));
    const metadataLines = tableHeaderIndex >= 0 ? lines.slice(0, tableHeaderIndex) : lines.slice(0, 30);
    const invoiceLineIndex = metadataLines.findIndex((line) => /(?:ф-?ра|фактура|invoice)/i.test(line));

    if (invoiceLineIndex >= 0) {
        for (
            let lineIndex = invoiceLineIndex;
            lineIndex < Math.min(metadataLines.length, invoiceLineIndex + 5);
            lineIndex += 1
        ) {
            const documentNumber =
                getDocumentNumberFromLine(metadataLines[lineIndex]) ||
                cleanDocumentNumber(metadataLines[lineIndex].match(standaloneDocumentNumberValueRegExp)?.[1] || '');

            if (documentNumber && /\d/.test(documentNumber)) {
                return documentNumber;
            }
        }
    }

    for (const line of metadataLines) {
        const documentNumber = getDocumentNumberFromLine(line);

        if (documentNumber) {
            return documentNumber;
        }
    }

    const customerOrderLine = metadataLines.find((line) => customerOrderDocumentNumberValueRegExp.test(line));
    const customerOrderDocumentNumber = customerOrderLine ? getDocumentNumberFromLine(customerOrderLine, true) : '';

    if (customerOrderDocumentNumber) {
        return customerOrderDocumentNumber;
    }

    const documentLineIndex = lines.findIndex((line) => documentNumberLineRegExp.test(line));
    if (documentLineIndex < 0) {
        return '';
    }

    for (let lineIndex = documentLineIndex; lineIndex <= documentLineIndex + 2 && lineIndex < lines.length; lineIndex += 1) {
        const documentNumber = getDocumentNumberFromLine(lines[lineIndex]);

        if (documentNumber) {
            return documentNumber;
        }
    }

    const fallbackDocumentNumber = cleanDocumentNumber(lines[documentLineIndex + 1] || '');

    return /\d/.test(fallbackDocumentNumber) ? fallbackDocumentNumber : '';
};

const parseSupplierName = (lines: PdfTextLine[]) => {
    const supplierLineIndex = lines.findIndex((line) => line.items.some((item) => supplierLabelRegExp.test(item.text)));
    const supplierItem = supplierLineIndex >= 0
        ? lines[supplierLineIndex].items.find((item) => supplierLabelRegExp.test(item.text))
        : undefined;
    const supplierX = supplierItem?.x;
    const sameLineSupplierMatch = supplierItem?.text.match(supplierRegExp);
    const sameLineSupplierName = cleanPdfMetadataValue(sameLineSupplierMatch?.[1] || '');

    if (isUsableSupplierName(sameLineSupplierName)) {
        return sameLineSupplierName;
    }

    if (supplierLineIndex >= 0 && supplierX !== undefined) {
        for (let lineIndex = supplierLineIndex + 1; lineIndex < Math.min(lines.length, supplierLineIndex + 8); lineIndex += 1) {
            const supplierNameItem = lines[lineIndex].items.find(
                (item) => item.x >= supplierX - 15 && companyNameRegExp.test(item.text),
            );

            if (supplierNameItem) {
                return cleanPdfMetadataValue(supplierNameItem.text);
            }
        }
    }

    return cleanPdfMetadataValue(lines.find((line) => companyNameRegExp.test(line.text))?.text || '');
};

const parsePdfMetadata = (lines: PdfTextLine[]): ParsedWarehousePdfMetadata => {
    const textLines = lines.map((line) => line.text);
    const supplierCandidate = parseSupplierName(lines);
    const supplierName = isUsableSupplierName(supplierCandidate) ? supplierCandidate : '';
    const receiptInfo = cleanPdfMetadataValue(textLines.find((line) => receiptRegExp.test(line)) || '');
    const documentNumber = parseDocumentNumber(textLines);

    return {
        documentNumber: documentNumber || undefined,
        receiptInfo: receiptInfo || undefined,
        supplierName: supplierName || undefined,
    };
};

const getPdfTextLines = async (file: File): Promise<PdfTextLine[]> => {
    const data = await file.arrayBuffer();
    const pdf = await getDocument({ data: new Uint8Array(data) }).promise;
    const lines: PdfTextLine[] = [];

    try {
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
            const page = await pdf.getPage(pageNumber);
            const textContent = await page.getTextContent();
            const pageLines = textContent.items.filter(isTextItem).reduce<PdfTextLine[]>((acc, item) => {
                const y = Math.round(item.transform[5]);
                const x = item.transform[4];
                const existingLine = acc.find((line) => Math.abs(line.y - y) <= 2);

                if (existingLine) {
                    existingLine.items.push({ text: item.str, x });
                } else {
                    acc.push({ items: [{ text: item.str, x }], pageNumber, text: '', y });
                }

                return acc;
            }, []);

            lines.push(...pageLines);
        }
    } finally {
        pdf.destroy();
    }

    return lines
        .sort((firstLine, secondLine) =>
            firstLine.pageNumber - secondLine.pageNumber || secondLine.y - firstLine.y
        )
        .map((line) => {
            const sortedItems = line.items.sort((firstItem, secondItem) => firstItem.x - secondItem.x);
            const text = sortedItems
                .map((item) => item.text)
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim();

            return {
                ...line,
                items: sortedItems,
                text,
            };
        })
        .filter((line) => line.text);
};

export const WarehousePdfImport = ({
    buttonClass = 'ms-3',
    hideButton = false,
    importFileRef,
    onImportSuccess,
    openFileDialogRef,
    warehouseId,
}: WarehousePdfImportProps) => {
    const { t } = useTranslation('translation');
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const dragDepthRef = useRef(0);
    const [isParsing, setIsParsing] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [parsedItems, setParsedItems] = useState<ParsedWarehousePdfItem[]>([]);
    const [parsedMetadata, setParsedMetadata] = useState<ParsedWarehousePdfMetadata>({});
    const [passportsByDescription, setPassportsByDescription] = useState<Record<string, PassportLookupResult>>({});
    const [allPassports, setAllPassports] = useState<ShortDevicePassport[]>([]);
    const [isLoadingPassportOptions, setIsLoadingPassportOptions] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [passportsErrorMessage, setPassportsErrorMessage] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [rejectedItemIndexes, setRejectedItemIndexes] = useState<number[]>([]);
    const [conflictingItemsByIndex, setConflictingItemsByIndex] = useState<Record<number, WarehousePdfImportInvalidItem>>({});
    const [mappingCorrectionModal, setMappingCorrectionModal] =
        useState<PassportMappingCorrectionModalState | null>(null);
    const [isSavingMappingCorrection, setIsSavingMappingCorrection] = useState(false);

    const activeParsedItems = useMemo(
        () => parsedItems.filter((_item, index) => !rejectedItemIndexes.includes(index)),
        [parsedItems, rejectedItemIndexes],
    );
    const activeParsedItemIndexes = useMemo(
        () => parsedItems.map((_item, index) => index).filter((index) => !rejectedItemIndexes.includes(index)),
        [parsedItems, rejectedItemIndexes],
    );
    const totalQuantity = useMemo(
        () => activeParsedItems.reduce((sum, item) => sum + (Number(normalizeDecimalValue(item.quantity)) || 0), 0),
        [activeParsedItems],
    );
    const translate = useCallback((key: string) => t(`table.${key}`), [t]);
    const documentNumberLabel = translate('warehousePdfImportDocumentNumber');
    const supplierLabel = translate('warehousePdfImportSupplier');

    useEffect(() => {
        if (!openFileDialogRef) {
            return;
        }

        openFileDialogRef.current = () => fileInputRef.current?.click();

        return () => {
            openFileDialogRef.current = null;
        };
    }, [openFileDialogRef]);

    const getPassportName = (passport?: PassportLookupResult) => {
        return passport?.name || passport?.passportName || passport?.passport || '';
    };

    const getPassportOptionValue = (passport: PassportLookupResult | ShortDevicePassport) => {
        return String(passport.id ?? passport.passportId ?? '');
    };

    const getSelectedPassportOption = (passport?: PassportLookupResult) => {
        if (!passport) {
            return null;
        }

        const passportValue = getPassportOptionValue(passport);
        const passportName = getPassportName(passport);

        const selectedOption = allPassports.find(
            (option) =>
                getPassportOptionValue(option) === passportValue ||
                (passportName && option.name === passportName),
        );

        if (selectedOption) {
            return {
                ...selectedOption,
                ...passport,
                name: passportName || selectedOption.name,
            } as ShortDevicePassport;
        }

        return passportName
            ? ({
                  ...passport,
                  id: passport.id,
                  passportId: String(passport.passportId ?? passport.id ?? passportName),
                  name: passportName,
              } as ShortDevicePassport)
            : null;
    };

    const cachePassportDetails = (passport: ShortDevicePassport) => {
        setAllPassports((passports) =>
            passports.map((option) =>
                getPassportOptionValue(option) === getPassportOptionValue(passport)
                    ? {
                          ...option,
                          ...passport,
                      }
                    : option,
            ),
        );
        setPassportsByDescription((passportsByDescriptionState) =>
            Object.entries(passportsByDescriptionState).reduce<Record<string, PassportLookupResult>>((acc, [key, passportByDescription]) => {
                acc[key] = getPassportOptionValue(passportByDescription) === getPassportOptionValue(passport)
                    ? {
                          ...passportByDescription,
                          ...passport,
                      }
                    : passportByDescription;

                return acc;
            }, {}),
        );
    };

    const loadOilPassportDetails = async (passport: ShortDevicePassport | null) => {
        if (!passport || !isOilPassport(passport) || passport.oilCapacity || !passport.id) {
            return;
        }

        try {
            const detailedPassport = await getPassportByPassportIdAndType(passport.id, 'OIL');
            cachePassportDetails({
                ...passport,
                ...detailedPassport,
            });
        } catch (error) {
            setPassportsErrorMessage(translate('warehousePdfImportPassportsError'));
        }
    };

    const getPassportDescription = (passport?: PassportLookupResult) => {
        return passport?.description || passport?.name || passport?.passportName || passport?.passport || '';
    };

    const loadPassportsByDescriptions = async (items: ParsedWarehousePdfItem[]) => {
        const descriptions = Array.from(new Set(items.map((item) => item.description).filter(Boolean)));

        if (descriptions.length === 0) {
            return {};
        }

        const passports = await getPassportsByDescriptions(descriptions);

        return passports.reduce<Record<string, PassportLookupResult>>((acc, passport, index) => {
            const passportDescription = getPassportDescription(passport as PassportLookupResult);
            const requestedDescription = descriptions[index];
            const key = getDescriptionKey(passportDescription || requestedDescription || '');

            if (key) {
                acc[key] = passport as PassportLookupResult;
            }

            return acc;
        }, {});
    };

    useEffect(() => {
        if (!showModal || allPassports.length > 0 || isLoadingPassportOptions) {
            return;
        }

        setIsLoadingPassportOptions(true);
        getAllShortPassports(false)
            .then(setAllPassports)
            .catch(() =>
                setPassportsErrorMessage(
                    translate('warehousePdfImportPassportsError'),
                ),
            )
            .finally(() => setIsLoadingPassportOptions(false));
    }, [allPassports.length, isLoadingPassportOptions, showModal, translate]);

    const parsePdfFile = async (file: File) => {
        setIsParsing(true);
        setErrorMessage('');
        setPassportsErrorMessage('');
        setParsedItems([]);
        setParsedMetadata({});
        setPassportsByDescription({});
        setRejectedItemIndexes([]);
        setConflictingItemsByIndex({});
        setMappingCorrectionModal(null);

        try {
            const lines = await getPdfTextLines(file);
            const items = parsePdfLines(lines);
            const metadata = parsePdfMetadata(lines);

            if (items.length === 0) {
                setErrorMessage(translate('warehousePdfImportNoItems'));
            }

            setParsedItems(items);
            setParsedMetadata(metadata);

            if (items.length > 0) {
                try {
                    setPassportsByDescription(await loadPassportsByDescriptions(items));
                } catch (error) {
                    setPassportsErrorMessage(translate('warehousePdfImportPassportsError'));
                }
            }

            setShowModal(true);
        } catch (error) {
            setErrorMessage(translate('warehousePdfImportError'));
            setShowModal(true);
        } finally {
            setIsParsing(false);
            setIsDragOver(false);
            dragDepthRef.current = 0;
        }
    };
    const parsePdfFileRef = useRef(parsePdfFile);

    useEffect(() => {
        parsePdfFileRef.current = parsePdfFile;
    });

    useEffect(() => {
        if (!importFileRef) {
            return;
        }

        importFileRef.current = (file: File) => parsePdfFileRef.current(file);

        return () => {
            importFileRef.current = null;
        };
    }, [importFileRef]);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            await parsePdfFile(file);
        }

        event.target.value = '';
    };

    const isFileDrag = (event: DragEvent<HTMLElement>) => {
        return Array.from(event.dataTransfer.types || []).includes('Files');
    };

    const handleDragEnter = (event: DragEvent<HTMLElement>) => {
        if (!isFileDrag(event) || isParsing) {
            return;
        }

        event.preventDefault();
        dragDepthRef.current += 1;
        setIsDragOver(true);
    };

    const handleDragOver = (event: DragEvent<HTMLElement>) => {
        if (!isFileDrag(event) || isParsing) {
            return;
        }

        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

        if (dragDepthRef.current === 0) {
            setIsDragOver(false);
        }
    };

    const handleDrop = async (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        dragDepthRef.current = 0;
        setIsDragOver(false);

        if (isParsing) {
            return;
        }

        const file = event.dataTransfer.files?.[0];

        if (file) {
            await parsePdfFile(file);
        }
    };

    const handleQuantityChange = (index: number, value: string) => {
        setParsedItems((items) =>
            items.map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...item,
                          quantity: value,
                      }
                    : item,
            ),
        );
    };

    const handleQuantityUnitChange = (index: number, value: QuantityUnit) => {
        setParsedItems((items) =>
            items.map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...item,
                          quantityUnit: value,
                          quantity: value === mlUnit ? '5' : '1',
                      }
                    : item,
            ),
        );

        if (value === mlUnit) {
            const item = parsedItems[index];
            const selectedPassport = getSelectedPassportOption(
                passportsByDescription[getDescriptionKey(item.description)],
            );
            void loadOilPassportDetails(selectedPassport);
        }
    };

    const handleHasContainerChange = (index: number, hasContainer: boolean) => {
        setParsedItems((items) =>
            items.map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...item,
                          hasContainer,
                      }
                    : item,
            ),
        );
    };

    const handleDeliveryPriceChange = (index: number, value: string) => {
        setParsedItems((items) =>
            items.map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...item,
                          deliveryPrice: value,
                      }
                    : item,
            ),
        );
    };

    const clearConflictForItem = (index: number) => {
        setConflictingItemsByIndex((itemsByIndex) => {
            const { [index]: _removedItem, ...restItems } = itemsByIndex;
            return restItems;
        });
    };

    const handlePassportChange = (
        description: string,
        selectedPassport: SingleValue<ShortDevicePassport>,
    ) => {
        const descriptionKey = getDescriptionKey(description);

        if (!selectedPassport) {
            setPassportsByDescription((passports) => {
                const { [descriptionKey]: _removedPassport, ...restPassports } = passports;
                return restPassports;
            });
            setParsedItems((items) =>
                items.map((item) =>
                    getDescriptionKey(item.description) === descriptionKey
                        ? {
                              ...item,
                              hasContainer: false,
                          }
                        : item,
                ),
            );
            return;
        }

        setPassportsByDescription((passports) => ({
            ...passports,
            [descriptionKey]: selectedPassport,
        }));

        if (!isLiquidMillilitrePassport(selectedPassport)) {
            setParsedItems((items) =>
                items.map((item) =>
                    getDescriptionKey(item.description) === descriptionKey
                        ? {
                              ...item,
                              hasContainer: supportsBottle(selectedPassport),
                              quantityUnit: countUnit,
                              quantity: '1',
                          }
                        : item,
                ),
            );
        } else {
            setParsedItems((items) =>
                items.map((item) =>
                    getDescriptionKey(item.description) === descriptionKey
                        ? {
                              ...item,
                              hasContainer: false,
                          }
                        : item,
                ),
            );
        }

        void loadOilPassportDetails(selectedPassport);
    };

    const openMappingCorrectionModal = (
        index: number,
        item: ParsedWarehousePdfItem,
        conflict: WarehousePdfImportInvalidItem,
    ) => {
        const selectedPassport = getSelectedPassportOption(
            passportsByDescription[getDescriptionKey(item.description)],
        );

        setMappingCorrectionModal({
            conflict,
            description: item.description,
            index,
            selectedPassport,
        });
    };

    const saveMappingCorrection = async () => {
        if (
            !mappingCorrectionModal?.selectedPassport?.id ||
            !mappingCorrectionModal.conflict.conflictingPassportId
        ) {
            return;
        }

        setIsSavingMappingCorrection(true);
        setPassportsErrorMessage('');

        try {
            await updatePassportDescriptionMapping({
                wrongPassportId: mappingCorrectionModal.conflict.conflictingPassportId,
                correctPassportId: mappingCorrectionModal.selectedPassport.id,
                description: mappingCorrectionModal.description,
            });

            setPassportsByDescription((passports) => ({
                ...passports,
                [getDescriptionKey(mappingCorrectionModal.description)]: mappingCorrectionModal.selectedPassport!,
            }));
            setParsedItems((items) =>
                items.map((item, index) =>
                    index === mappingCorrectionModal.index
                        ? {
                              ...item,
                              hasContainer: supportsBottle(mappingCorrectionModal.selectedPassport),
                          }
                        : item,
                ),
            );
            clearConflictForItem(mappingCorrectionModal.index);
            setMappingCorrectionModal(null);
        } catch (error) {
            setPassportsErrorMessage(translate('warehousePdfImportMappingSaveError'));
        } finally {
            setIsSavingMappingCorrection(false);
        }
    };

    const toggleRejectedItem = (index: number) => {
        if (isSaving) {
            return;
        }

        setRejectedItemIndexes((indexes) =>
            indexes.includes(index)
                ? indexes.filter((rejectedIndex) => rejectedIndex !== index)
                : [...indexes, index],
        );
    };

    const stopRowToggle = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    const getImportErrorResponse = (error: unknown): WarehousePdfImportErrorResponse | undefined => {
        if (!error || typeof error !== 'object') {
            return undefined;
        }

        return error as WarehousePdfImportErrorResponse;
    };

    const buildImportPayload = (): WarehousePdfImportPayload => {
        return {
            documentNumber: parsedMetadata.documentNumber,
            supplierName: parsedMetadata.supplierName,
            warehouseId,
            items: parsedItems.filter((_item, index) => !rejectedItemIndexes.includes(index)).map((item, index) => {
                const selectedPassport = getSelectedPassportOption(
                    passportsByDescription[getDescriptionKey(item.description)],
                );

                return {
                    batchCode: `${getBatchCode()}-${index + 1}`,
                    description: item.description,
                    passport: selectedPassport
                        ? {
                              id: selectedPassport.id,
                              passportId: selectedPassport.passportId,
                              name: selectedPassport.name,
                          }
                        : undefined,
                    deliveryPrice: normalizeDecimalValue(item.deliveryPrice),
                    quantity: getImportQuantity(item.quantity),
                    quantityUnit: item.quantityUnit,
                    hasContainer: supportsBottle(selectedPassport)
                        ? item.hasContainer ?? true
                        : false,
                };
            }),
        };
    };

    const handleModalCancel = () => {
        setShowModal(false);
    };

    const handleModalSave = async () => {
        setIsSaving(true);
        setPassportsErrorMessage('');
        setConflictingItemsByIndex({});

        if (activeParsedItems.length === 0) {
            setPassportsErrorMessage(translate('warehousePdfImportNoItems'));
            setIsSaving(false);
            return;
        }

        const hasMissingOilCapacity = activeParsedItems.some((item) => {
            const selectedPassport = getSelectedPassportOption(
                passportsByDescription[getDescriptionKey(item.description)],
            );

            return item.quantityUnit === mlUnit && (!isOilPassport(selectedPassport) || !selectedPassport?.oilCapacity);
        });

        if (hasMissingOilCapacity) {
            setPassportsErrorMessage(translate('warehousePdfImportPassportsError'));
            setIsSaving(false);
            return;
        }

        const hasInvalidMlQuantity = activeParsedItems.some((item) => {
            const quantity = getImportQuantity(item.quantity);
            return item.quantityUnit === mlUnit
                && (!Number.isInteger(quantity) || quantity <= 0 || quantity % 5 !== 0);
        });
        if (hasInvalidMlQuantity) {
            setPassportsErrorMessage(translate('warehouseManualQuantityInvalidMlStep'));
            setIsSaving(false);
            return;
        }
        const hasInvalidCount = activeParsedItems.some((item) => {
            const quantity = getImportQuantity(item.quantity);
            return item.quantityUnit === countUnit && (!Number.isInteger(quantity) || quantity <= 0);
        });
        if (hasInvalidCount) {
            setPassportsErrorMessage(translate('warehouseManualQuantityInvalidCount'));
            setIsSaving(false);
            return;
        }

        let imported = false;
        try {
            await importWarehousePdfDocument(buildImportPayload());
            imported = true;
            setShowModal(false);
            await onImportSuccess?.();
        } catch (error) {
            if (imported) {
                toast.warning(translate('warehousePdfImportRefreshError'));
                return;
            }
            const duplicateMessage = getWarehouseImportConflictMessage(error, t);
            if (duplicateMessage) {
                setPassportsErrorMessage(duplicateMessage);
                return;
            }
            const importError = getImportErrorResponse(error);

            if (importError?.invalidItems?.length) {
                const conflictsByOriginalIndex = importError.invalidItems.reduce<Record<number, WarehousePdfImportInvalidItem>>(
                    (conflicts, invalidItem) => {
                        const originalItemIndex = activeParsedItemIndexes[invalidItem.index];

                        if (originalItemIndex !== undefined) {
                            conflicts[originalItemIndex] = invalidItem;
                        }

                        return conflicts;
                    },
                    {},
                );

                setConflictingItemsByIndex(conflictsByOriginalIndex);
                setPassportsErrorMessage(translate('warehousePdfImportConflictError'));
            } else {
                setPassportsErrorMessage(translate('warehousePdfImportSaveError'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const isDropButtonActive = isHovering || isDragOver;
    const buttonStateClass = isDragOver
        ? 'btn-warning text-dark border-warning'
        : `btn-success ${isHovering ? 'text-white border-success' : ''}`;

    return (
        <>
            {!hideButton && (
                <div
                    className='position-relative d-inline-block'
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => {
                        setIsHovering(false);
                        setIsDragOver(false);
                        dragDepthRef.current = 0;
                    }}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <CustomButton
                        type='button'
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isParsing}
                        icon={isParsing ? undefined : isDropButtonActive ? faCloudUploadAlt : faFileImport}
                        title={
                            isDropButtonActive
                                ? translate('warehousePdfImportDropFilesHere')
                                : translate('warehousePdfImportButton')
                        }
                        iconClass='me-2'
                        className={`${buttonStateClass} text-nowrap outline-none ${buttonClass}`}
                        style={{ width: '13rem' }}
                    />
                </div>
            )}
            <Form.Control
                ref={fileInputRef}
                type='file'
                accept='application/pdf,.pdf'
                onChange={handleFileChange}
                disabled={isParsing}
                className='d-none'
            />

            <Modal show={showModal} onHide={() => undefined} backdrop='static' keyboard={false} size='xl' centered>
                <Modal.Header>
                    <Modal.Title id='contained-modal-title-vcenter' className='w-100'>
                        <div className='d-flex justify-content-between align-items-center gap-3'>
                            <span>
                                {documentNumberLabel}: {parsedMetadata.documentNumber || '-'}
                            </span>
                            <span className='text-end'>
                                {supplierLabel}: {parsedMetadata.supplierName || '-'}
                            </span>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {errorMessage ? (
                        <p className='text-danger mb-0'>{errorMessage}</p>
                    ) : (
                        <>
                            <Table responsive striped bordered hover size='sm' className='align-middle small' style={{ tableLayout: 'fixed' }}>
                                <colgroup>
                                    <col style={{ width: '41%' }} />
                                    <col style={{ width: '4%' }} />
                                    <col style={{ width: '23%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '14%' }} />
                                    <col style={{ width: '8%' }} />
                                </colgroup>
                                <thead>
                                <tr>
                                    <th className='text-wrap border-start-0 border-end-0'>{translate('warehousePdfImportDescription')}</th>
                                    <th className='border-start-0 border-end-0' />
                                    <th className='text-wrap border-start-0 border-end-0'>{translate('warehousePdfImportPassport')}</th>
                                    <th className='text-wrap text-center border-start-0 border-end-0'>{translate('warehousePdfImportDeliveryPrice')}</th>
                                    <th className='text-wrap text-center border-start-0 border-end-0'>{translate('warehousePdfImportQuantity')}</th>
                                    <th className='text-wrap text-center border-start-0 border-end-0'>{translate('hasBottle')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {parsedItems.map((item, index) => {
                                    const descriptionKey = getDescriptionKey(item.description);
                                    const selectedPassport = passportsByDescription[descriptionKey];
                                    const selectedPassportOption = getSelectedPassportOption(selectedPassport);
                                    const isRejected = rejectedItemIndexes.includes(index);
                                    const conflictingItem = conflictingItemsByIndex[index];
                                    const canUseMl = isLiquidMillilitrePassport(selectedPassportOption);
                                    const rowSupportsBottle = supportsBottle(selectedPassportOption);
                                    const enteredQuantity = getImportQuantity(item.quantity);
                                    const invalidQuantity = !Number.isInteger(enteredQuantity)
                                        || enteredQuantity <= 0
                                        || (item.quantityUnit === mlUnit && enteredQuantity % 5 !== 0);

                                    return (
                                    <tr
                                        key={`${item.description}-${index}`}
                                        onClick={() => toggleRejectedItem(index)}
                                        className='cursor-pointer'
                                        style={
                                            conflictingItem
                                                ? {
                                                      '--bs-table-bg': '#f5c2c7',
                                                      '--bs-table-accent-bg': '#f5c2c7',
                                                      backgroundColor: '#f5c2c7',
                                                  } as React.CSSProperties
                                                : isRejected
                                                ? {
                                                      '--bs-table-bg': '#f8d7da',
                                                      '--bs-table-accent-bg': '#f8d7da',
                                                      backgroundColor: '#f8d7da',
                                                  } as React.CSSProperties
                                                : undefined
                                        }
                                    >
                                        <td
                                            className='text-wrap lh-sm'
                                            style={{
                                                overflow: 'visible',
                                                overflowWrap: 'normal',
                                                whiteSpace: 'normal',
                                                wordBreak: 'normal',
                                            }}
                                        >
                                            {item.description}
                                            {conflictingItem && (
                                                <>
                                                    <p className='text-danger fw-bold mb-1 mt-1'>
                                                        {translate('warehousePdfImportConflictPassport')}: {conflictingItem.conflictingPassport || '-'}
                                                    </p>
                                                    <Button
                                                        size='sm'
                                                        variant='warning'
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openMappingCorrectionModal(index, item, conflictingItem);
                                                        }}
                                                    >
                                                        {translate('warehousePdfImportFixMapping')}
                                                    </Button>
                                                </>
                                            )}
                                        </td>
                                        <td className='text-center'>
                                            <FontAwesomeIcon icon={faArrowRight} />
                                        </td>
                                        <td className='text-break text-wrap lh-sm' onClick={stopRowToggle}>
                                            <Select<ShortDevicePassport, false>
                                                className='text-start'
                                                isClearable
                                                isDisabled={isLoadingPassportOptions || isSaving}
                                                isSearchable
                                                maxMenuHeight={180}
                                                menuPortalTarget={document.body}
                                                onChange={(passport) => handlePassportChange(item.description, passport)}
                                                options={allPassports}
                                                placeholder={
                                                    isLoadingPassportOptions
                                                        ? translate('warehousePdfImportLoadingPassports')
                                                        : translate('warehousePdfImportSelectPassport')
                                                }
                                                styles={{
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                    container: (base) => ({ ...base, minWidth: 0 }),
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: 36,
                                                        height: 'auto',
                                                    }),
                                                    input: (base) => ({ ...base, margin: 0, padding: 0 }),
                                                    option: (base) => ({
                                                        ...base,
                                                        lineHeight: 1.2,
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    }),
                                                    singleValue: (base) => ({
                                                        ...base,
                                                        lineHeight: 1.2,
                                                        maxWidth: 'none',
                                                        overflow: 'visible',
                                                        position: 'static',
                                                        transform: 'none',
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        alignItems: 'flex-start',
                                                        flexWrap: 'wrap',
                                                        overflow: 'visible',
                                                        paddingTop: 4,
                                                        paddingBottom: 4,
                                                    }),
                                                }}
                                                getOptionLabel={(passport) => passport.name}
                                                getOptionValue={getPassportOptionValue}
                                                value={selectedPassportOption}
                                            />
                                        </td>
                                        <td className='text-center' onClick={stopRowToggle}>
                                            <Form.Control
                                                className='px-1 text-center'
                                                type='text'
                                                inputMode='decimal'
                                                disabled={isSaving}
                                                value={item.deliveryPrice}
                                                onChange={(event) => handleDeliveryPriceChange(index, event.target.value)}
                                            />
                                        </td>
                                        <td className='text-center' onClick={stopRowToggle}>
                                            <div className='d-flex gap-1'>
                                                <Form.Control
                                                    className='px-1 text-center'
                                                    type='number'
                                                    inputMode='numeric'
                                                    min={item.quantityUnit === mlUnit ? 5 : 1}
                                                    step={item.quantityUnit === mlUnit ? 5 : 1}
                                                    isInvalid={item.quantity.trim() !== '' && invalidQuantity}
                                                    disabled={isSaving}
                                                    value={item.quantity}
                                                    onChange={(event) => handleQuantityChange(index, event.target.value)}
                                                    style={{ minWidth: 0, flex: '1 1 auto' }}
                                                />
                                                <Form.Select
                                                    className='px-1'
                                                    disabled={isSaving || !canUseMl}
                                                    value={canUseMl ? item.quantityUnit : countUnit}
                                                    onChange={(event) =>
                                                        handleQuantityUnitChange(index, event.target.value as QuantityUnit)
                                                    }
                                                    style={{ width: 54, minWidth: 54, flex: '0 0 54px' }}
                                                >
                                                    <option value={countUnit}>бр</option>
                                                    <option value={mlUnit}>мл</option>
                                                </Form.Select>
                                            </div>
                                        </td>
                                        <td className='text-center' onClick={stopRowToggle}>
                                            <Form.Check
                                                className='d-flex justify-content-center mb-0'
                                                type='checkbox'
                                                disabled={isSaving || !rowSupportsBottle}
                                                checked={rowSupportsBottle && (item.hasContainer ?? true)}
                                                onChange={(event) =>
                                                    handleHasContainerChange(index, event.currentTarget.checked)
                                                }
                                                title={translate('hasBottle')}
                                            />
                                        </td>
                                    </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                            {passportsErrorMessage && <p className='text-danger mb-2'>{passportsErrorMessage}</p>}
                            <p className='text-end fw-bold mb-0'>
                                {translate('warehousePdfImportTotal')}: {totalQuantity}
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={handleModalCancel} disabled={isSaving}>
                        {translate('warehousePdfImportCancel')}
                    </Button>
                    <Button variant='success' onClick={handleModalSave} disabled={isSaving}>
                        {translate('warehousePdfImportSave')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={!!mappingCorrectionModal}
                onHide={() => setMappingCorrectionModal(null)}
                backdrop='static'
                keyboard={false}
                centered
            >
                <Modal.Header closeButton={!isSavingMappingCorrection}>
                    <Modal.Title>{translate('warehousePdfImportFixMapping')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className='mb-3'>
                        <Form.Label>{translate('warehousePdfImportDescription')}</Form.Label>
                        <div className='border rounded p-2 bg-light'>{mappingCorrectionModal?.description}</div>
                    </Form.Group>
                    <Form.Group className='mb-3'>
                        <Form.Label>{translate('warehousePdfImportWrongPassport')}</Form.Label>
                        <div className='border rounded p-2 bg-light'>
                            {mappingCorrectionModal?.conflict.conflictingPassport || '-'}
                        </div>
                    </Form.Group>
                    <Form.Group className='mb-0'>
                        <Form.Label>{translate('warehousePdfImportCorrectPassport')}</Form.Label>
                        <Select<ShortDevicePassport, false>
                            className='text-start'
                            isClearable
                            isDisabled={isSavingMappingCorrection}
                            isSearchable
                            maxMenuHeight={180}
                            menuPortalTarget={document.body}
                            onChange={(passport) =>
                                setMappingCorrectionModal((current) =>
                                    current ? { ...current, selectedPassport: passport } : current,
                                )
                            }
                            options={allPassports}
                            placeholder={translate('warehousePdfImportSelectPassport')}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            getOptionLabel={(passport) => passport.name}
                            getOptionValue={getPassportOptionValue}
                            value={mappingCorrectionModal?.selectedPassport || null}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant='secondary'
                        onClick={() => setMappingCorrectionModal(null)}
                        disabled={isSavingMappingCorrection}
                    >
                        {translate('warehousePdfImportCancel')}
                    </Button>
                    <Button
                        variant='success'
                        onClick={saveMappingCorrection}
                        disabled={isSavingMappingCorrection || !mappingCorrectionModal?.selectedPassport?.id}
                    >
                        {translate('warehousePdfImportSave')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
